/**
 * Cross-tab delivery. Every open tab holds its own stream and store, so
 * without this a completion would chime and pop in all of them.
 *
 * A notification names the tab whose request started the work
 * (`origin_tab`, the id every API call sends as `X-Nuclearn-Tab`); that
 * tab delivers at once and the others leave it alone. If it never claims —
 * closed, reloaded (which mints a new id) — a sibling stands in after
 * `ORIGIN_FALLBACK_DELAY_MS`. Claims live in localStorage, which another
 * browser (or device) signed in as the same user cannot see, so a sibling
 * only stands in for an origin tab this browser has seen; every tab records
 * itself on mount for that. Notifications no tab started fall back to
 * first-to-claim, where a background tab waits a beat so the tab the user
 * is looking at wins.
 */

import { TAB_ID } from "src/helpers/tabId";
import type { UserNotification } from "./types";
import { tabInBackground } from "./visibility";

const CLAIM_PREFIX = "nuclearn:notified:";
const CLAIM_TTL_MS = 15 * 60 * 1000;
/** Serialises claims across tabs: localStorage reads aren't atomic across
 *  renderer processes, so a bare check-and-set can let two tabs through. */
const CLAIM_LOCK = "nuclearn:notification-claim";
/** How long a background tab yields to a possibly-focused sibling. */
export const BACKGROUND_CLAIM_DELAY_MS = 300;
/** How long other tabs give the origin tab to claim before standing in. */
export const ORIGIN_FALLBACK_DELAY_MS = 2000;
/** Spread added to deferred claims when no lock can serialise them. */
export const UNLOCKED_CLAIM_JITTER_MS = 500;

/** Whether claims can be serialised across tabs (Web Locks needs HTTPS). */
export const claimsAreSerialised = (): boolean => !!navigator.locks;

let lastPrune = 0;

function pruneClaims(now: number): void {
  if (now - lastPrune < CLAIM_TTL_MS) return;
  lastPrune = now;
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key?.startsWith(CLAIM_PREFIX)) continue;
      const at = Number(localStorage.getItem(key));
      if (!Number.isFinite(at) || now - at > CLAIM_TTL_MS) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // Storage unavailable: nothing to prune.
  }
}

/**
 * Storage failures (private mode, quota) fall back to delivering — a
 * duplicate beats a silent miss.
 */
function claimInStorage(id: string): boolean {
  const now = Date.now();
  pruneClaims(now);
  try {
    const key = CLAIM_PREFIX + id;
    if (localStorage.getItem(key) !== null) return false;
    localStorage.setItem(key, String(now));
    return true;
  } catch {
    return true;
  }
}

const SEEN_PREFIX = "nuclearn:tab-seen:";
const SEEN_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Record that this tab exists in this browser, so siblings can later tell
 * an origin tab of theirs that closed or reloaded from a tab in another
 * browser. One key per tab, so concurrent tabs never overwrite each other;
 * entries older than `SEEN_TTL_MS` are dropped. Only the id's existence is
 * stored — the id itself stays in module memory (see tabId.ts).
 */
export function rememberThisTab(now: number = Date.now()): void {
  try {
    localStorage.setItem(SEEN_PREFIX + TAB_ID, String(now));
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key?.startsWith(SEEN_PREFIX)) continue;
      const at = Number(localStorage.getItem(key));
      if (!Number.isFinite(at) || now - at > SEEN_TTL_MS) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // Storage unavailable: siblings cannot stand in for this tab.
  }
}

/** Whether `tabId` was opened in this browser; a tab elsewhere never is. */
export function originTabIsKnown(tabId: string): boolean {
  try {
    return localStorage.getItem(SEEN_PREFIX + tabId) !== null;
  } catch {
    return false;
  }
}

/**
 * Resolves true if this tab is the one that should deliver `id`. Web Locks
 * needs a secure context; plain-HTTP deployments get the bare check-and-set
 * and rely on `deliveryDelayMs` jittering the tabs apart instead.
 */
export async function claimNotification(id: string): Promise<boolean> {
  const locks = navigator.locks;
  if (!locks) return claimInStorage(id);
  try {
    return await locks.request(CLAIM_LOCK, () => claimInStorage(id));
  } catch {
    // Locks unavailable (some private modes): no worse than before.
    return claimInStorage(id);
  }
}

/**
 * How long this tab should wait before trying to claim `n`; 0 = now, null =
 * never (the origin is a tab in another browser, whose claim this one could
 * not see; the notification still reaches the bell). Deferred claims that
 * can't be serialised get a random spread so sibling timers don't fire into
 * the storage check at the same instant. Only background tabs are jittered:
 * there is one focused tab, so a focused claim never races another focused
 * one, and jittering it would let a background sibling's spread overlap the
 * head start it is meant to yield.
 */
export function deliveryDelayMs(
  n: Pick<Partial<UserNotification>, "origin_tab">,
  background: boolean = tabInBackground(),
  serialised: boolean = claimsAreSerialised(),
  originKnown: boolean = !!n.origin_tab && originTabIsKnown(n.origin_tab),
): number | null {
  if (n.origin_tab && n.origin_tab === TAB_ID) return 0;
  if (n.origin_tab && !originKnown) return null;
  const yieldToFocused = background ? BACKGROUND_CLAIM_DELAY_MS : 0;
  const delay = (n.origin_tab ? ORIGIN_FALLBACK_DELAY_MS : 0) + yieldToFocused;
  if (!background || serialised) return delay;
  return delay + Math.floor(Math.random() * UNLOCKED_CLAIM_JITTER_MS);
}
